import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withProps,
  withState,
  withHooks,
} from '@ngrx/signals';
import { computed, inject, linkedSignal } from '@angular/core';
import {
  withRequestStatus,
  setReadStatus,
  setCreateStatus,
  setUpdateStatus,
  setDeleteStatus,
} from '../../../../shared/store-utils/request-status.feature';
import { withErrorHandler } from '../../../../shared/store-utils/error-handler.feature';
import { RolesManagementService } from '../services';
import { AddRoleDialogComponent } from '../components/add-role-dialog/add-role-dialog.component';
import { IUser } from '../../users-management/interfaces';
import { UsersManagementStore } from '../../users-management/store/users-management.store';
import { withDevtools, withUndoRedo } from '@angular-architects/ngrx-toolkit';
import { withUiStates } from '../../../../shared/store-utils/ui-state.feature';
import { IRole, IPermissionNode, IPermission, IAddRole } from '../interfaces';
import * as helper from '../roles-management.helper';
import * as cnst from '../constants';
import {
  setAllEntities,
  setEntities,
  withEntities,
  setEntity,
  removeEntity,
} from '@ngrx/signals/entities';
import { withCRUD } from '../../../../shared/store-utils/crud.feature';
import { withDataSync } from '../../../../shared/store-utils/data-sync.feature';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { pipe, concatMap, tap, exhaustMap, switchMap, take, filter, map } from 'rxjs';
import { tapResponse } from '@ngrx/operators';
import { UNDO_CLICKED } from '../../../../shared/constants';
import { MatDialogRef } from '@angular/material/dialog';
import { FormGroup } from '@angular/forms';
import { SharedUIService } from '../../../../shared/services';
import { TranslationService } from '../../../shared/services/translation.service';
import { ClientPlatform } from '../enum';
import { withUtils } from '../../../../shared/store-utils/utils.feature';
import { ADMIN } from '../../../core/constants';
import { StateStatus } from '../../../core/enums/state-status.enum';
import { DataSyncService } from '../../../../shared/services/data-sync/data-sync.service';
import { UPDATE_DATA_TYPE } from '../../../../shared/services/data-sync/enums';

interface RolesManagementState {
  tempRole: IRole | null;
  selectedRole: IRole | null;
  permissionsChanged: boolean;
  unassignedUsers: IUser[];
  openDetailStates: Record<string, boolean>;
  previousRolesState: IRole[];
}

const initialState: RolesManagementState = {
  tempRole: null,
  selectedRole: null,
  permissionsChanged: false,
  unassignedUsers: [],
  openDetailStates: {},
  previousRolesState: [],
};

export const RolesManagementStore = signalStore(
  { providedIn: 'root' },
  withDevtools('RolesManagementStore'),
  withState(initialState),
  withEntities<IRole>(),
  withRequestStatus(),
  withErrorHandler(),
  withUiStates(),
  withCRUD<IRole>(),
  withUtils<IRole>(),
  withUndoRedo(),
  withProps(() => ({
    _rolesManagementService: inject(RolesManagementService),
    _usersStore: inject(UsersManagementStore),
    _sharedUIService: inject(SharedUIService),
    _syncService: inject(DataSyncService),
    _translationService: inject(TranslationService),
  })),
  withComputed(
    ({
      tempRole,
      searchQuery,
      selectedRole,
      unassignedUsers,
      userFilter,
      _usersStore,
      entities,
      _translationService,
    }) => {
      const isAdmin = computed(() => {
        const role = tempRole();
        return role && role.name === ADMIN;
      });

      const assignedUsersDetails = linkedSignal(() => {
        const role = tempRole();
        const users = _usersStore.entities();
        return (role?.assignedUsers || []).map(
          (id) => users.find((u) => u.id === id)!,
        );
      });

      const filteredAssignedUsers = linkedSignal(() => {
        const base = assignedUsersDetails();
        const uf = userFilter();
        if (uf === 'notAssigned') {
          return [];
        }
        const list = base;

        return searchQuery() ? helper.searchUsers(list, searchQuery()) : list;
      });

      const filteredUnassignedUsers = linkedSignal(() => {
        const base = unassignedUsers();
        const uf = userFilter();
        if (uf === 'assigned') {
          return [];
        }
        const list = base;

        return searchQuery() ? helper.searchUsers(list, searchQuery()) : list;
      });

      const desktopPermissionsEnabled = computed(
        () => tempRole()!.permissions.desktop.length > 0,
      );
      const mobilePermissionsEnabled = computed(
        () => tempRole()!.permissions.mobile.length > 0,
      );

      const platformIcon = computed(() => {
        const desktop = tempRole()!.permissions.desktop.length > 0;
        const mobile = tempRole()!.permissions.mobile.length > 0;

        if (desktop && !mobile) return cnst.DESKTOP_PLATFORM;
        if (!desktop && mobile) return cnst.MOBILE_PLATFORM;
        if (desktop && mobile) return cnst.DEFAULT_PLATFORM;
        return cnst.DESKTOP_PLATFORM;
      });

      const desktopTree = computed<IPermissionNode[]>(() =>
        helper.permissionToNodes(
          tempRole()?.permissions.desktop ?? [],
          cnst.desktopPermissions,
        ),
      );
      const mobileTree = computed<IPermissionNode[]>(() =>
        helper.permissionToNodes(
          tempRole()?.permissions.mobile ?? [],
          cnst.mobilePermissions,
        ),
      );

      const selectedRoleUserCountText = computed(() => {
        let count: number = 0;
        if (selectedRole()) {
          count = selectedRole()!.assignedUsers.length;
        }
        if (tempRole()) {
          count = tempRole()!.assignedUsers.length;
        }
        
        if (!count) {
          return _translationService.getTranslation('none');
        }
        
        const countKey = count > 1 ? 'people' : 'person';
        return _translationService.getTranslation(countKey).pipe(
          map((translation: string) => `${count} ${translation}`)
        );
      });

      const filteredRolesBySearch = linkedSignal(() => {
        const query = searchQuery().toLowerCase();
        if (!query) {
          return entities();
        }

        return entities().filter((role) =>
          role.name.toLowerCase().includes(query),
        );
      });

      return {
        isAdmin,
        filteredAssignedUsers,
        filteredUnassignedUsers,
        desktopPermissionsEnabled,
        mobilePermissionsEnabled,
        platformIcon,
        desktopTree,
        mobileTree,
        selectedRoleUserCountText,
        filteredRolesBySearch,
      };
    },
  ),
  withMethods((store) => {
    function setSelectedRole(role: IRole) {
      patchState(store, { selectedRole: role, tempRole: role });
    }

    function setPlatform(platform: ClientPlatform, checked: boolean) {
      let otherPlatformPermissions: IPermission[] = [];
      if (platform === ClientPlatform.DESKTOP) {
        const permissionsToAssign = checked
          ? helper.nodesToPermission(cnst.desktopPermissions)
          : [];
        otherPlatformPermissions = helper.nodesToPermission(
          cnst.mobilePermissions,
        );
        patchState(store, {
          permissionsChanged: true,
          tempRole: {
            ...store.tempRole()!,
            permissions: {
              ...store.tempRole()!.permissions,
              desktop: permissionsToAssign,
            },
          },
        });
      } else if (platform === ClientPlatform.MOBILE) {
        const permissionsToAssign = checked
          ? helper.nodesToPermission(cnst.mobilePermissions)
          : [];
        otherPlatformPermissions = helper.nodesToPermission(
          cnst.desktopPermissions,
        );
        patchState(store, {
          permissionsChanged: true,
          tempRole: {
            ...store.tempRole()!,
            permissions: {
              ...store.tempRole()!.permissions,
              mobile: permissionsToAssign,
            },
          },
        });
      }
      if (
        store.tempRole()?.permissions.desktop.length === 0 &&
        store.tempRole()?.permissions.mobile.length === 0
      ) {
        const otherPlatform =
          platform === ClientPlatform.DESKTOP
            ? ClientPlatform.MOBILE
            : ClientPlatform.DESKTOP;
        patchState(store, {
          tempRole: {
            ...store.tempRole()!,
            permissions: {
              ...store.tempRole()!.permissions,
              [otherPlatform]: otherPlatformPermissions,
            },
          },
        });
      }
      updateTempRoleIcon();
    }

    function updateTempRoleIcon() {
      if (!store.tempRole()) return;

      patchState(store, {
        tempRole: {
          ...store.tempRole()!,
          icon: {
            ...store.tempRole()!.icon,
            name: store.platformIcon(),
          },
        },
      });
    }

    function togglePermission(
      node: IPermissionNode,
      checked: boolean,
      platform: ClientPlatform,
    ) {
      const role = store.tempRole();
      if (!role) return;

      const currentPermissions =
        platform === ClientPlatform.DESKTOP
          ? role.permissions.desktop
          : role.permissions.mobile;

      const permissionTree =
        platform === ClientPlatform.DESKTOP
          ? cnst.desktopPermissions
          : cnst.mobilePermissions;

      const updatedPermissions = helper.togglePermissionNode(
        node,
        checked,
        currentPermissions,
        permissionTree,
      );

      if (platform === ClientPlatform.DESKTOP) {
        patchState(store, {
          tempRole: {
            ...role,
            permissions: {
              ...role.permissions,
              desktop: updatedPermissions,
            },
          },
          permissionsChanged: true,
        });
      } else {
        patchState(store, {
          tempRole: {
            ...role,
            permissions: {
              ...role.permissions,
              mobile: updatedPermissions,
            },
          },
          permissionsChanged: true,
        });
      }
    }

    function addUserToRole(user: Partial<IUser>) {
      if (!store.tempRole()) return;

      patchState(store, {
        permissionsChanged: true,
        tempRole: {
          ...store.tempRole()!,
          assignedUsers: [...store.tempRole()!.assignedUsers, user.id!],
        },
        unassignedUsers: store
          .unassignedUsers()
          .filter((u) => u.id !== user.id),
      });
    }

    function removeUserFromRole(user: IUser, role: IRole) {
      const recoverUserRole = {
        ...user,
        userRole: role,
      };
      patchState(store, {
        permissionsChanged: true,
        tempRole: {
          ...store.tempRole()!,
          assignedUsers: store
            .tempRole()!
            .assignedUsers.filter((id) => id !== user.id),
        },
        unassignedUsers: [...store.unassignedUsers(), recoverUserRole],
      });

      if (role.id === store.tempRole()!.id) {
        patchState(store, {
          selectedRole: {
            ...role,
            assignedUsers: role.assignedUsers.filter((id) => id !== user.id),
          },
        });
      }
    }

    function submitRole(
      form: FormGroup,
      dialogRef: MatDialogRef<AddRoleDialogComponent>,
    ) {
      if (form.get('name')?.hasError('roleNameExists')) {
        store._sharedUIService.openSuccessDialog(
          cnst.roleNameExistsErrorDialogData,
        );
        return;
      }
      if (!store.permissionsChanged() && !store.selectedRole()) {
        store._sharedUIService.openSuccessDialog(
          cnst.permissionsChangedDialogData,
        );
        return;
      }

      dialogRef.close(form.getRawValue());
    }

    function toggleRoleDetails(role: IRole) {
      const currentOpenState = store.openDetailStates()[role.id] || false;

      patchState(store, {
        openDetailStates: {
          ...store.openDetailStates(),
          [role.id]: !currentOpenState,
        },
        selectedRole: role,
        tempRole: role,
      });
    }

    function isRoleOpen(roleId: string): boolean {
      return !!store.openDetailStates()[roleId];
    }

    function closeRoleOverlay(roleId: string) {
      patchState(store, {
        openDetailStates: {
          ...store.openDetailStates(),
          [roleId]: false,
        },
        searchQuery: '',
      });
    }

    function upsertRole(role: IRole) {
      patchState(store, setEntity(role));
    }

    function _reassignUsers(newRole: IRole) {
      if (!newRole.assignedUsers.length) return;

      const currentEntities = store.entities();
      const updatedEntities = [...currentEntities];

      patchState(store, {
        previousRolesState: [...currentEntities],
      });

      newRole.assignedUsers.forEach((userId) => {
        const rolesWithUser = currentEntities.filter(
          (role) =>
            role.id !== newRole.id && role.assignedUsers.includes(userId),
        );

        rolesWithUser.forEach((role) => {
          const roleIndex = updatedEntities.findIndex((r) => r.id === role.id);
          if (roleIndex > -1) {
            updatedEntities[roleIndex] = {
              ...role,
              assignedUsers: role.assignedUsers.filter((id) => id !== userId),
            };
          }
        });
      });

      patchState(store, setAllEntities(updatedEntities));
    }

    const loadRoles = store.loadRxMethod()(
      () => store._rolesManagementService.getRoles(),
      { beforeLoad: () => patchState(store, setReadStatus(StateStatus.PENDING)) },
    );

    const deleteRole = rxMethod<IRole>(
      pipe(
        exhaustMap((role) =>
          store._translationService.getTranslation(cnst.SNACKBAR_DELETE_MESSAGE).pipe(
            take(1),
            switchMap((translatedMessage) =>
              store._sharedUIService
                .openConfirmDialog(helper.getDeleteRoleDialogData(role.name))
                .pipe(
                  filter((confirmed) => !!confirmed),
                  tap(() =>
                    patchState(store, removeEntity(role.id), setDeleteStatus(StateStatus.PENDING))
                  ),
                  exhaustMap(() =>
                    store._sharedUIService
                      .withUndoRequestAction(
                        store._rolesManagementService.deleteRole(role.id),
                        translatedMessage
                      )
                      .pipe(
                        tapResponse({
                          next: (result) => {
                            if (result === UNDO_CLICKED) {
                              patchState(store, setEntities([role]), setDeleteStatus(StateStatus.FULFILLED));
                            }
                          },
                          error: (error: Error) =>
                            patchState(store, setDeleteStatus({ error: error.message })),
                        })
                      )
                  )
                )
            )
          )
        )
      )
    );

    const addRoleDialog = rxMethod<void>(
      pipe(
        concatMap(() => {
          patchState(store, {
            tempRole: helper.createTempRole(),
            unassignedUsers: [...store._usersStore.entities()],
          });

          return store._translationService.getTranslation(cnst.SNACKBAR_CREATE_MESSAGE).pipe(
            take(1),
            switchMap((translatedMessage) =>
              store.runDialogWorkflow(store._sharedUIService, {
                dialogComponent: AddRoleDialogComponent,
                dialogConfig: {
                  width: '1125px',
                  panelClass: 'add-role-dialog',
                  disableClose: true,
                },
                onBeforeServiceCall: (result) => {
                  const updatedRole = helper.createRoleObj(
                    result as IAddRole,
                    store.tempRole()!,
                  );

                  _reassignUsers(updatedRole);

                  const updatedRoles = helper.upsertRole(
                    store.entities(),
                    updatedRole,
                  );

                  patchState(store, setAllEntities(updatedRoles), {
                    tempRole: updatedRole,
                  });
                },
                getServiceCall: (dialogResult: IAddRole) =>
                  store._rolesManagementService.addRole(
                    helper.createRoleObj(dialogResult, store.tempRole()!),
                  ),
                onSuccess: () => {
                  patchState(store, setCreateStatus(StateStatus.FULFILLED));
                },
                withUndo: true,
                undoMessage: translatedMessage,
              })
            )
          );
        }),
      ),
    );

    const openEditRoleModal = rxMethod<IRole>(
      pipe(
        tap((role) => {
          closeRoleOverlay(role.id);
          patchState(store, {
            tempRole: role,
            unassignedUsers: helper.filterAssignedUsers(
              store._usersStore.entities(),
              role.assignedUsers,
            ),
            permissionsChanged: false,
          });
        }),
        exhaustMap(() =>
          store._translationService.getTranslation(cnst.SNACKBAR_UPDATE_MESSAGE).pipe(
            take(1),
            switchMap((translatedMessage) =>
              store.runDialogWorkflow(store._sharedUIService, {
                dialogComponent: AddRoleDialogComponent,
                dialogConfig: {
                  width: '1125px',
                  panelClass: 'add-role-dialog',
                  data: {
                    selectedTabIndex: store.tempRole()?.name === ADMIN ? 1 : 0,
                  },
                },
                onBeforeServiceCall: (result) => {
                  const updatedRole = helper.createRoleObj(
                    result as IAddRole,
                    store.tempRole()!,
                  );

                  _reassignUsers(updatedRole);

                  const updatedRoles = helper.upsertRole(
                    store.entities(),
                    updatedRole,
                  );

                  patchState(store, setAllEntities(updatedRoles), {
                    tempRole: updatedRole,
                  });
                },
                getServiceCall: (result) =>
                  store._rolesManagementService.updateRole(
                    helper.createRoleObj(result as IAddRole, store.tempRole()!),
                  ),
                onSuccess: () => {
                  patchState(store, setUpdateStatus(StateStatus.FULFILLED));
                },
                withUndo: true,
                undoMessage: translatedMessage,
              })
            )
          )
        ),
      ),
    );

    return {
      setSelectedRole,
      setPlatform,
      togglePermission,
      addUserToRole,
      removeUserFromRole,
      submitRole,
      toggleRoleDetails,
      isRoleOpen,
      closeRoleOverlay,
      loadRoles,
      deleteRole,
      addRoleDialog,
      openEditRoleModal,
      upsertRole,
    };
  }),
  withDataSync({
    updateDataTypes: {
      [UPDATE_DATA_TYPE.USER_ROLES_UPDATED]: (data, store) => {
        const { userId, roleIds } = data;
        const { oldRoleId, newRoleId } = roleIds;

        const roles = store.entities();

        const updateRoleEntities = () =>
          roles.map((role: IRole) => {
            if (role.id === oldRoleId) {
              return {
                ...role,
                assignedUsers: role.assignedUsers.filter((id) => id !== userId),
              };
            }

            if (role.id === newRoleId && !role.assignedUsers.includes(userId)) {
              return {
                ...role,
                assignedUsers: [...role.assignedUsers, userId],
              };
            }

            return role;
          });

        const updateRoleInDetailState = (
          key: 'selectedRole' | 'tempRole',
          roleId: string,
          action: 'remove' | 'add',
        ) => {
          const current = store[key]();
          if (current?.id !== roleId) return;

          const alreadyAssigned = current.assignedUsers.includes(userId);
          const updatedUsers =
            action === 'remove'
              ? current.assignedUsers.filter((id: string) => id !== userId)
              : alreadyAssigned
              ? current.assignedUsers
              : [...current.assignedUsers, userId];

          patchState(store, {
            [key]: {
              ...current,
              assignedUsers: updatedUsers,
            },
          });
        };

        if (oldRoleId && !newRoleId) {
          patchState(store, setAllEntities(updateRoleEntities()));
          updateRoleInDetailState('selectedRole', oldRoleId, 'remove');
          updateRoleInDetailState('tempRole', oldRoleId, 'remove');
          return;
        }

        if (oldRoleId && newRoleId) {
          patchState(store, setAllEntities(updateRoleEntities()));

          updateRoleInDetailState('selectedRole', oldRoleId, 'remove');
          updateRoleInDetailState('selectedRole', newRoleId, 'add');

          updateRoleInDetailState('tempRole', oldRoleId, 'remove');
          updateRoleInDetailState('tempRole', newRoleId, 'add');

          patchState(store, {
            unassignedUsers: store
              .unassignedUsers()
              .filter((user: IUser) => user.id !== userId),
          });
        }
      },
    },
  }),
  withHooks({
    onInit({ loadRoles, initializeDataSync }) {
      loadRoles();
      initializeDataSync();
    },
  }),
);
